import { FC, useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "motion/react";

import { InviteContent } from "@/pages/invites/components/invite-content";
import { useInvitePageState } from "@/pages/invites/invites-page.hooks";
import { FullScreenOverlay } from "@/components/full-screen-overlay";
import { useAppStore } from "@/store";
import { appRoutes } from "@/app/router/appRoutes";
import styled from "@emotion/styled";
import { windowModule } from "@/modules/window";

export const InvitesPage: FC = () => {
  const { inviteId } = useParams<{ inviteId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const { store } = useAppStore();
  const viewerName =
    store.account.myAccount?.profileDisplayName ??
    store.account.myAccount?.profileEmailAddress ??
    null;

  const {
    invite,
    kind,
    hasAccepted,
    isAccepting,
    acceptError,
    statusTitle,
    statusBody,
    successMessage,
    handleAccept,
  } = useInvitePageState({ inviteId, token });

  const viewKind = kind;

  const handleDismiss = useCallback(() => {
    const homePath = appRoutes.notesList({}).path;
    windowModule.navigateToAndRefresh({ url: new URL(homePath) });
  }, []);

  const handleOpen = useCallback(() => {
    if (!invite) {
      return;
    }

    if (invite.sharedItemKind === "NOTE") {
      const notesPath = appRoutes.notesView({ params: { noteId: invite.sharedItemId } }).path;
      windowModule.navigateToAndRefresh({ url: new URL(notesPath) });
      return;
    }

    if (invite.sharedItemKind === "COLLECTION") {
      const collectionsPath = appRoutes.collectionsView({
        params: { collectionId: invite.sharedItemId },
      }).path;
      windowModule.navigateToAndRefresh({ url: new URL(collectionsPath) });
      return;
    }

    const homePath = appRoutes.notesList({}).path;
    windowModule.navigateToAndRefresh({ url: new URL(homePath) });
  }, [invite]);

  const contentState = useMemo(
    () => ({
      viewKind,
      invite,
      hasAccepted,
      isAccepting,
      acceptError,
      statusTitle,
      statusBody,
      successMessage,
      viewerName,
      onAccept: handleAccept,
      onOpen: hasAccepted ? handleOpen : undefined,
      onDismiss: handleDismiss,
    }),
    [
      acceptError,
      handleAccept,
      handleDismiss,
      handleOpen,
      hasAccepted,
      invite,
      isAccepting,
      statusBody,
      statusTitle,
      successMessage,
      viewerName,
      viewKind,
    ]
  );

  return (
    <FullScreenOverlay>
      <MotionDiv initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.1 }}>
        <InviteContent {...contentState} />
      </MotionDiv>
    </FullScreenOverlay>
  );
};

const MotionDiv = styled(motion.div)`
  min-width: 100%;
  min-height: 100%;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -100px;
`;
